/**
 * The job of this piece of code is to manage the view state
 * of the ApiError component, from a parent component.
 *
 */

import {
  reactive, toRefs, Ref, watch,
} from '@vue/composition-api'

interface State {
  /**
   * Indicates whether or not to display the ApiError component
   * (when there is an error, it will never show the ApiError if
   * there is no error)
   * */
  showResponseError: boolean;
  /**
   * A flag that gets set when an error is shown/hidden,
   * updates AFTER any visibility transition in ApiError
   * has finished.
   * */
  responseErrorIsShown: boolean | undefined;
  /**
   * A flag that gets set immediately, BEFORE a change
   * transition has occurred in ApiError.
   * */
  showingResponseError: boolean;
}

const initialState: State = {
  showingResponseError: false,
  showResponseError: true,
  responseErrorIsShown: undefined,
}

/**
 * The job of this function is to provide code to manage the view state
 * of an ApiError component, from within a parent component.
 *
 * @param hasResponseError a reactive boolean variable set by consuming component
 */
export default function (hasResponseError: Ref<boolean>) {
  const state = reactive({ ...initialState })

  const externalState = reactive({ hasResponseError })

  watch(() => externalState.hasResponseError, (hasErr: boolean) => {
    if (hasErr) {
      state.showingResponseError = state.showResponseError
      return
    }
    state.showingResponseError = false
  })

  /**
   * Use to trigger general hiding/showing of the ApiError comp.
   * from the parent. Note that it will only affect a change when
   * there actually is an error (e.g. a toggle will only have an
   * effect when there is an error)
   */
  function toggleShowApiError() {
    state.showResponseError = !state.showResponseError
    if (state.showResponseError) {
      state.showingResponseError = externalState.hasResponseError
    }
  }

  /**
   * Flag to indicate whether or not the ApiError component is
   * currently shown. Should be set AFTER any show/hide transition
   * has finished, by wiring it to the ApiError components 'shown'/'hidden'
   * events, in the parents template.
   * @param shown
   */
  function errorVisibilityHasChangedTo(shown: boolean) {
    state.responseErrorIsShown = shown
    state.showingResponseError = shown
  }

  return {
    ...toRefs(state), errorVisibilityHasChangedTo, toggleShowApiError,
  }
}
